// ========================================
// ユーティリティ関数
// ========================================

import { DECK_SIZE } from './constants';
import { fillDeck } from './deckConstruction';
import { PREBUILT_DECKS } from '../decks/prebuiltDecks';
import type { CardData, CardWithId, MonsterInstance, HandCard } from '../types';

// グローバルカウンター: uniqueIdの一意性を保証
let uniqueIdCounter = 0;

/**
 * 一意のIDを生成
 * @param cardId - カードID
 * @returns ユニークID
 */
export const generateUniqueId = (cardId: string): string => {
  uniqueIdCounter++;
  return `${cardId}-${Date.now()}-${uniqueIdCounter}-${Math.random()}`;
};

/**
 * 配列をシャッフル
 */
export const shuffle = <T>(array: T[]): T[] => {
  const newArray = [...array];
  for (let i = newArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
  }
  return newArray;
};

/**
 * デッキを生成（カードプールからランダムに40枚）
 * @param cardPool - カードプール
 * @returns シャッフルされたデッキ
 */
export const createDeck = (cardPool: CardData[] = []): HandCard[] => {
  const deck: HandCard[] = [];
  const availableCards = cardPool.filter(c =>
    c.type === 'monster' || c.type === 'magic' || c.type === 'field' || c.type === 'phasecard' as string
  );

  if (availableCards.length === 0) {
    console.error('利用可能なカードがありません');
    return [];
  }

  return shuffle(fillDeck(deck, availableCards, generateUniqueId));
};

/**
 * カードが特定のカテゴリを持っているか判定
 * カテゴリ名の【】は自動的に除去して比較する
 * @param card - カードオブジェクト
 * @param categoryName - カテゴリ名（【】付きでも可）
 * @returns カテゴリを持っている場合true
 */
export const hasCategory = (card: CardData | null | undefined, categoryName: string): boolean => {
  if (!card || !card.category) return false;
  // 【】を除去して正規化
  const normalized = categoryName.replace(/【|】/g, '');
  if (Array.isArray(card.category)) {
    return card.category.includes(normalized);
  }
  // 文字列の場合（古い形式への対応）
  return (card.category as string).includes(normalized) || (card.category as string).includes(categoryName);
};

/** リンクボーナス情報 */
interface LinkedBonus {
  atk: number;
  hp: number;
}

/** エンドフェイズダメージ情報 */
interface LinkedEndPhaseDamage {
  damage: number;
  linkedWith: string;
  sourceCardName: string;
}

/** モンスターインスタンスの拡張（内部用） */
interface MonsterInstanceInternal extends MonsterInstance {
  usedSkillThisTurn: boolean;
  linkedTo: string | null;
  linkedBonus: LinkedBonus;
  linkedEndPhaseDamage: LinkedEndPhaseDamage[];
  charges: (CardData | { card?: CardWithId; type: string })[];
}

/**
 * モンスターインスタンスを生成
 * @param card - カードデータ
 * @returns モンスターインスタンス
 */
export const createMonsterInstance = (card: CardData & { uniqueId?: string }): MonsterInstanceInternal => ({
  ...card,
  // カード種類IDではなく個体IDを保持する。新規生成のみIDを発行する。
  uniqueId: card.uniqueId || generateUniqueId(card.id),
  currentHp: card.hp ?? 0,
  currentAttack: card.attack ?? 0,
  canAttack: false,
  charges: [],
  chargeCount: 0,
  statusEffects: [],
  originalCard: card,
  usedSkillThisTurn: false,
  // 【魂結】リンク関連プロパティ
  linkedTo: null, // リンク相手のuniqueId
  linkedBonus: { atk: 0, hp: 0 }, // リンクによる自身へのボーナス
  linkedEndPhaseDamage: [], // リンクによるエンドフェイズダメージ [{damage, linkedWith, sourceCardName}]
});

/** ユーザーデッキのカード情報 */
interface UserDeckCard {
  cardId: string;
  rarity?: string;
}

/** ユーザーデッキ */
interface UserDeck {
  cards: UserDeckCard[];
  name?: string;
}

/**
 * 固定デッキからカードインスタンスを生成
 * @param deckId - デッキID（'random', 'fire', 'water', 'light', 'balanced'）
 * @param cardPool - カードプール（CSVから読み込んだ全カードデータ）
 * @returns シャッフルされたデッキ
 */
export const createDeckFromPrebuilt = (deckId: string, cardPool: CardData[] = []): HandCard[] => {
  const deckConfig = PREBUILT_DECKS[deckId as keyof typeof PREBUILT_DECKS];

  // ランダムまたは不明なデッキIDの場合は従来のランダム生成
  if (!deckConfig || deckConfig.cards === null) {
    return createDeck(cardPool);
  }

  // 固定デッキからカードインスタンスを生成
  const deck: HandCard[] = [];
  for (const cardId of deckConfig.cards) {
    const cardData = cardPool.find(c => c.id === cardId);
    if (cardData) {
      deck.push({
        ...cardData,
        uniqueId: generateUniqueId(cardId),
      });
    } else {
      console.warn(`カードが見つかりません: ${cardId}`);
    }
  }

  // デッキが40枚に満たない場合、ランダムで補充
  if (deck.length < DECK_SIZE) {
    console.warn(`デッキが${deck.length}枚しかありません。ランダムで補充します。`);
    return shuffle(fillDeck(deck, cardPool, generateUniqueId));
  }

  return shuffle(deck);
};

/**
 * ユーザー作成デッキからカードインスタンスを生成
 * @param userDeck - ユーザーデッキ（playerData.userDecksの要素）
 * @param cardPool - カードプール（CSVから読み込んだ全カードデータ）
 * @returns シャッフルされたデッキ
 */
export const createDeckFromUserDeck = (userDeck: UserDeck | null | undefined, cardPool: CardData[] = []): HandCard[] => {
  if (!userDeck || !userDeck.cards || !Array.isArray(userDeck.cards)) {
    console.error('無効なユーザーデッキ:', userDeck);
    return createDeck(cardPool);
  }

  const deck: HandCard[] = [];
  for (const deckCard of userDeck.cards) {
    const cardData = cardPool.find(c => c.id === deckCard.cardId);
    if (cardData) {
      deck.push({
        ...cardData,
        rarity: deckCard.rarity, // レアリティを保持
        uniqueId: generateUniqueId(deckCard.cardId),
      } as HandCard & { rarity?: string });
    } else {
      console.warn(`カードが見つかりません: ${deckCard.cardId}`);
    }
  }

  // デッキが40枚に満たない場合、ランダムで補充
  if (deck.length < DECK_SIZE) {
    console.warn(`ユーザーデッキが${deck.length}枚しかありません。ランダムで補充します。`);
    return shuffle(fillDeck(deck, cardPool, generateUniqueId));
  }

  return shuffle(deck);
};
